import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Badge, Form, Row, Col } from 'react-bootstrap';
import { paymentService } from '../../services/api';
import { Payment } from '../../types';
import { toast } from 'react-toastify';
import { PlusIcon, EyeIcon, EditIcon, TrashIcon } from '../common/Icons';
import PaymentForm from './PaymentForm';

const PaymentList: React.FC = () => {
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPayment, setEditingPayment] = useState<Payment | null>(null);
  const [filters, setFilters] = useState({
    type: '',
    method: '',
    search: '',
  });

  useEffect(() => {
    fetchPayments();
  }, [filters]);

  const fetchPayments = async () => {
    try {
      setLoading(true);
      const response = await paymentService.getAll(filters);
      setPayments(response.data.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des règlements');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce règlement ?')) {
      try {
        await paymentService.delete(id);
        toast.success('Règlement supprimé avec succès');
        fetchPayments();
      } catch (error) {
        toast.error('Erreur lors de la suppression');
      }
    }
  };

  const getTypeBadge = (type: string) => {
    const variants: { [key: string]: string } = {
      received: 'success',
      sent: 'primary',
    };
    
    const labels: { [key: string]: string } = {
      received: 'Reçu',
      sent: 'Envoyé',
    };

    return <Badge bg={variants[type] || 'secondary'}>{labels[type] || type}</Badge>;
  };

  const getMethodLabel = (method: string) => {
    const labels: { [key: string]: string } = {
      cash: 'Espèces',
      check: 'Chèque',
      transfer: 'Virement',
      card: 'Carte',
    };
    return labels[method] || method;
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Règlements</h2>
        <Button variant="primary" onClick={() => setShowForm(true)}>
          <PlusIcon className="me-2" />
          Nouveau règlement
        </Button>
      </div>

      <Card>
        <Card.Header>
          <Row>
            <Col md={3}>
              <Form.Control
                type="text"
                placeholder="Rechercher..."
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
              />
            </Col>
            <Col md={3}>
              <Form.Select
                value={filters.type}
                onChange={(e) => setFilters(prev => ({ ...prev, type: e.target.value }))}
              >
                <option value="">Tous les types</option>
                <option value="received">Reçu</option>
                <option value="sent">Envoyé</option>
              </Form.Select>
            </Col>
            <Col md={3}>
              <Form.Select
                value={filters.method}
                onChange={(e) => setFilters(prev => ({ ...prev, method: e.target.value }))}
              >
                <option value="">Toutes les méthodes</option>
                <option value="cash">Espèces</option>
                <option value="check">Chèque</option>
                <option value="transfer">Virement</option>
                <option value="card">Carte</option>
              </Form.Select>
            </Col>
          </Row>
        </Card.Header>
        <Card.Body>
          {loading ? (
            <div className="text-center">Chargement...</div>
          ) : (
            <Table responsive>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Type</th>
                  <th>Client/Fournisseur</th>
                  <th>Méthode</th>
                  <th>Référence</th>
                  <th>Montant</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {payments.map((payment) => (
                  <tr key={payment.id}>
                    <td>{new Date(payment.date).toLocaleDateString()}</td>
                    <td>{getTypeBadge(payment.type)}</td>
                    <td>{payment.client?.name || payment.supplier?.name}</td>
                    <td>{getMethodLabel(payment.method)}</td>
                    <td>{payment.reference}</td>
                    <td className={payment.type === 'received' ? 'text-success' : 'text-danger'}>
                      {payment.type === 'received' ? '+' : '-'}{parseFloat(payment.amount.toString()).toFixed(2)} MAD
                    </td>
                    <td>
                      <Button variant="outline-info" size="sm" className="me-1">
                        <EyeIcon />
                      </Button>
                      <Button 
                        variant="outline-warning" 
                        size="sm" 
                        className="me-1"
                        onClick={() => {
                          setEditingPayment(payment);
                          setShowForm(true);
                        }}
                      >
                        <EditIcon />
                      </Button>
                      <Button 
                        variant="outline-danger" 
                        size="sm"
                        onClick={() => handleDelete(payment.id)}
                      >
                        <TrashIcon />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      <PaymentForm
        show={showForm}
        onHide={() => {
          setShowForm(false);
          setEditingPayment(null);
        }}
        onSuccess={() => {
          fetchPayments();
          setShowForm(false);
          setEditingPayment(null);
        }}
        payment={editingPayment}
      />
    </div>
  );
};

export default PaymentList;